import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_API_URL || '';

class ApiClient {
  private client: AxiosInstance;
  private refreshClient: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: ((token: string) => void)[] = [];

  constructor() {
    this.client = axios.create({
      baseURL: BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.refreshClient = axios.create({
      baseURL: BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('adminJwt');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error?.response?.status;
        const originalRequest = error?.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

        if (status === 401 && originalRequest) {
          const refreshToken = localStorage.getItem('refreshToken');

          if (!refreshToken) {
            this.clearTokensAndRedirect();
            return Promise.reject(error);
          }

          if (originalRequest._retry) {
            this.clearTokensAndRedirect();
            return Promise.reject(error);
          }
          originalRequest._retry = true;

          if (this.isRefreshing) {
            return new Promise((resolve) => {
              this.subscribeTokenRefresh((newToken: string) => {
                originalRequest.headers = originalRequest.headers || {};
                (originalRequest.headers as any).Authorization = `Bearer ${newToken}`;
                resolve(this.client(originalRequest));
              });
            });
          }

          this.isRefreshing = true;
          try {
            const res = await this.refreshClient.post<{ data: { accessToken: string } }>(
              '/api/v1/user-admin/refresh',
              { refreshToken }
            );
            const newToken = res.data.data.accessToken;
            localStorage.setItem('adminJwt', newToken);

            this.onRefreshed(newToken);

            originalRequest.headers = originalRequest.headers || {};
            (originalRequest.headers as any).Authorization = `Bearer ${newToken}`;

            return this.client(originalRequest);
          } catch (refreshErr) {
            this.clearTokensAndRedirect();
            return Promise.reject(refreshErr);
          } finally {
            this.isRefreshing = false;
          }
        }

        const message = error.response?.data?.message || 'An error occurred';
        toast.error(message);
        return Promise.reject(error);
      }
    );
  }

  private subscribeTokenRefresh(cb: (token: string) => void) {
    this.refreshSubscribers.push(cb);
  }
  private onRefreshed(token: string) {
    this.refreshSubscribers.forEach((cb) => cb(token));
    this.refreshSubscribers = [];
  }

  private clearTokensAndRedirect() {
    localStorage.removeItem('adminJwt');
    localStorage.removeItem('refreshToken');
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}

export default new ApiClient();